import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { Plus, ChevronRight } from "lucide-react";
import api from "@/lib/api";
import { Icon } from "@/lib/icons";
import { CATEGORY_IMG } from "@/lib/media";
import ThreadCard from "@/components/ThreadCard";

export default function Category() {
  const { categorySlug } = useParams();
  const [cat, setCat] = useState(null);
  const [threads, setThreads] = useState([]);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    api.get(`/categories/${categorySlug}`).then((r) => setCat(r.data)).catch(() => setNotFound(true));
    api.get("/threads", { params: { category: categorySlug, sort: "trending", limit: 40 } })
      .then((r) => setThreads(r.data)).catch(() => {});
  }, [categorySlug]);

  if (notFound) return <div className="max-w-3xl mx-auto py-24 px-6 text-center text-mutedplum">Category not found. <Link to="/community" className="text-teal font-semibold">Back to Community</Link></div>;
  if (!cat) return <div className="py-24 text-center text-mutedplum">Loading…</div>;

  return (
    <div>
      <div className="relative h-56 sm:h-64 overflow-hidden">
        <img src={CATEGORY_IMG[cat.slug]} alt={cat.name} className="absolute inset-0 h-full w-full object-cover" />
        <div className="absolute inset-0 bg-plum/55" />
        <div className="relative mx-auto max-w-7xl px-4 sm:px-6 h-full flex flex-col justify-end pb-8">
          <nav className="text-cream/70 text-sm flex items-center gap-1.5 mb-2">
            <Link to="/community" className="hover:text-cream">Community</Link><ChevronRight className="h-4 w-4" /><span className="text-cream">{cat.name}</span>
          </nav>
          <div className="flex items-center gap-4">
            <span className="inline-flex h-14 w-14 items-center justify-center rounded-full bg-cream/90 text-teal shrink-0">
              <Icon name={cat.icon} className="h-6 w-6" />
            </span>
            <div>
              <h1 className="font-display text-4xl sm:text-5xl text-cream">{cat.name}</h1>
              <p className="text-cream/80">{cat.thread_count} {cat.thread_count === 1 ? "discussion" : "discussions"}</p>
            </div>
          </div>
        </div>
      </div>

      <div className="mx-auto max-w-7xl px-4 sm:px-6 py-10">
        <div className="flex items-center justify-between mb-6">
          <p className="text-mutedplum text-lg max-w-2xl">{cat.description}</p>
          <Link to={`/new-discussion?category=${cat.slug}`} data-testid="category-new-discussion"
            className="inline-flex items-center gap-2 rounded-full bg-teal px-6 py-3 font-semibold text-white hover:bg-teal-dark transition-colors shrink-0">
            <Plus className="h-5 w-5" /> New discussion
          </Link>
        </div>
        <h2 className="font-display text-2xl text-plum mb-6">Discussions in {cat.name}</h2>
        {threads.length === 0 ? (
          <div className="bg-white rounded-3xl border border-rose/30 p-12 text-center text-mutedplum">
            No discussions yet. <Link to={`/new-discussion?category=${cat.slug}`} className="text-teal font-semibold">Start the first one.</Link>
          </div>
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {threads.map((t, i) => <ThreadCard key={t.thread_id} thread={t} index={i} />)}
          </div>
        )}
      </div>
    </div>
  );
}
